/*
 * DossierPanel — right-rail panel showing the dossier as it fills.
 *
 * The panel is STRICTLY SCOPED to what the user is doing right now — it
 * is NOT a tabbed view of the whole 154-field universe. The active scope
 * is derived from the persisted landing-chooser route + a layer toggle:
 *
 *   • Solve-a-problem route, Layer 1  → the 23 problem fields (Section E),
 *                                       counted "X / 23 problem fields".
 *   • Solve-a-problem route, Deep     → the deep-scoping fields, grouped by
 *                                       phase (Phase 1 · Company shell =
 *                                       H/I/M/N/O, Phase 3 · Outcomes = K/P),
 *                                       counted against the visible deep set.
 *   • Map-the-company route           → the company fields (A/B/C/D + F/G).
 *
 * The L1 ⇄ Deep toggle (the two-moon control in the header) lets the user
 * flip between the problem layer and its deep-scoping layer on the problem
 * route. No section tabs, no whole-company tally — you see only the section
 * set that matches the route + layer you're in.
 *
 * State machine per row is owned by <DossierRow>. Routing/recommendation is
 * surfaced by the <RecommendationPill> on the composer — not here.
 */

import React, { useMemo, useState } from 'react';
import { Target, Layers } from 'lucide-react';

import type {
  CompanyContext,
  FieldDef,
  FieldStateEntry,
  SectionLetter,
} from '../lib/dossier-types';

import { DossierRow } from './DossierRow';

// Section sets per route/layer. Mirrors the backend:
//   L1 company = a/b/c/d/f/g ; L1 problem = e ; L2 deep = h/i/m/n/o (Phase 1)
//   + k/p (Phase 3). Phase 2 (per-problem card loop) is not dossier-shaped.
const COMPANY_SECTIONS: SectionLetter[] = ['a', 'b', 'c', 'd', 'f', 'g'];
const L2_PHASE_1_SECTIONS: SectionLetter[] = ['h', 'i', 'm', 'n', 'o'];
const L2_PHASE_3_SECTIONS: SectionLetter[] = ['k', 'p'];

const CAPTURED_STATES = new Set(['user_confirmed', 'user_corrected', 'ai_guessed']);

export interface DossierPanelProps {
  fields: FieldDef[];
  sections: Partial<Record<SectionLetter, string>>;
  ctx: CompanyContext;
  activeField: string | null;       // currently asked-about by the Inquirer
  capturedCount: number;
  totalCount: number;
  onCommit: (fieldKey: string, value: unknown, action: 'confirm' | 'correct') => void;
}

export function DossierPanel({
  fields,
  sections,
  ctx,
  activeField,
  onCommit,
}: DossierPanelProps) {
  const fieldStates = ctx.field_states ?? {};

  // ── Active scope: route + layer ───────────────────────────────────
  // Authoritative landing-chooser route (persisted by /select-route),
  // falling back to the per-turn emphasis `_path`. This — NOT a tab —
  // decides which section set the panel shows.
  const routeEntry = fieldStates['_selected_route'] as { value?: string } | undefined;
  const pathEntry = fieldStates['_path'] as { value?: string } | undefined;
  const route =
    ((routeEntry?.value ?? pathEntry?.value) as 'problem' | 'company' | 'explore' | undefined) ?? null;
  const isProblem = route === 'problem';
  const isCompany = route === 'company';

  // L1 ⇄ Deep toggle (the two-moon control). Only meaningful on the
  // problem route, where the problem (L1) has a deep-scoping layer (L2).
  const [deepView, setDeepView] = useState(false);
  const showDeep = isProblem && deepView;

  const isCaptured = (key: string): boolean =>
    CAPTURED_STATES.has((fieldStates[key] as { state?: string } | undefined)?.state ?? '');

  // ── The scoped section list for the current (route, layer) ────────
  // Each entry is [sectionLetter, label, fields]. Group labels for the
  // deep view carry the phase framing the user asked for.
  type Group = { key: string; label: string; fields: FieldDef[] };

  const groups: Group[] = useMemo(() => {
    const inSection = (letters: SectionLetter[], opts?: { onlyVisible?: boolean }) =>
      fields.filter(f =>
        letters.includes(f.section)
        && f.key !== 'e.flag_reason'
        && f.key !== 'a.industry_detail'   // folds under a.industry
        && (opts?.onlyVisible ? (f.hidden === undefined ? true : !f.hidden) || isCaptured(f.key) : true),
      );

    if (isProblem && !showDeep) {
      // Layer 1 — the user-facing problem fields (Section E). Show them all
      // (conditional ones appear as empty rows until they apply), EXCEPT the
      // internal/auto fields the user never fills by hand:
      //   • clarity_self_signal — Orbit SCORES clarity itself from the problem
      //     description (`_never_visible_to_user`); it must not render as a
      //     "Tap to fill" row.
      //   • exploration_interests — an EXPLORE-route field, not problem scoping.
      //   • flag_reason — internal.
      const E_INTERNAL = new Set([
        'e.flag_reason', 'e.clarity_self_signal', 'e.exploration_interests',
      ]);
      const e = fields.filter(f => f.section === 'e' && !E_INTERNAL.has(f.key));
      return e.length ? [{ key: 'e', label: 'Your problem', fields: e }] : [];
    }

    if (showDeep) {
      // Deep scoping — phase-grouped. Show the fields in play (visible or
      // already captured) so the rail adapts to THIS problem instead of
      // dumping all 93 L2 fields.
      const p1 = inSection(L2_PHASE_1_SECTIONS, { onlyVisible: true });
      const p3 = inSection(L2_PHASE_3_SECTIONS, { onlyVisible: true });
      const out: Group[] = [];
      if (p1.length) out.push({ key: 'l2p1', label: 'Phase 1 · Company shell', fields: p1 });
      if (p3.length) out.push({ key: 'l2p3', label: 'Phase 3 · Outcomes & success', fields: p3 });
      return out;
    }

    // Company route (and any non-problem fallback) — the company fields,
    // section by section, in natural order. No problem/deep fields.
    const byLetter: Partial<Record<SectionLetter, FieldDef[]>> = {};
    for (const f of inSection(COMPANY_SECTIONS)) {
      (byLetter[f.section] ??= []).push(f);
    }
    return COMPANY_SECTIONS
      .filter(l => (byLetter[l] ?? []).length > 0)
      .map(l => ({ key: l, label: sections[l] ?? l.toUpperCase(), fields: byLetter[l] ?? [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, sections, isProblem, showDeep, fieldStates]);

  // ── Header count — against the active scope only ──────────────────
  const headerCount = useMemo(() => {
    const all = groups.flatMap(g => g.fields);
    const captured = all.reduce((n, f) => n + (isCaptured(f.key) ? 1 : 0), 0);
    return { captured, total: all.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, fieldStates]);

  const countLabel = showDeep
    ? 'deep-scope fields'
    : isProblem
      ? 'problem fields'
      : isCompany
        ? 'company fields'
        : 'fields';

  return (
    <aside className="aa-dossier" aria-label="Dossier">
      <header className="aa-dossier-header">
        <div className="aa-dossier-headline">
          <span className="aa-dossier-count">{headerCount.captured} / {headerCount.total}</span>
          <span className="aa-dossier-count-label">{countLabel}</span>
        </div>

        {/* L1 ⇄ Deep toggle — only on the problem route, where the problem
            has a deep-scoping layer. Flip to read either layer of the SAME
            problem: Problem (the 23 fields) ⇄ Deep scope (the deep layer). */}
        {isProblem && (
          <div className="aa-dossier-layer-toggle" role="tablist" aria-label="Scope layer">
            <button
              type="button"
              role="tab"
              aria-selected={!deepView}
              className={'aa-dossier-layer' + (!deepView ? ' aa-dossier-layer--on' : '')}
              onClick={() => setDeepView(false)}
            >
              <Target size={13} aria-hidden="true" />
              <span>Problem</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={deepView}
              className={'aa-dossier-layer' + (deepView ? ' aa-dossier-layer--on' : '')}
              onClick={() => setDeepView(true)}
            >
              <Layers size={13} aria-hidden="true" />
              <span>Deep scope</span>
            </button>
          </div>
        )}
      </header>

      <div className="aa-dossier-scroll">
        {groups.map(group => (
          <section key={group.key} className="aa-dossier-section">
            <div className="aa-dossier-section-label">{group.label}</div>
            <div className="aa-dossier-rows">
              {group.fields.map(field => {
                const sectionKey = `section_${field.section}` as keyof CompanyContext;
                const section = ctx[sectionKey] as Record<string, unknown> | undefined;
                const value = section?.[field.key.split('.')[1]];
                let secondary: string | null = null;
                if (field.key === 'a.industry') {
                  const detail = (ctx.section_a as Record<string, unknown>)?.industry_detail;
                  if (typeof detail === 'string' && detail.trim()) secondary = detail;
                }
                return (
                  <DossierRow
                    key={field.key}
                    field={field}
                    value={value}
                    stateEntry={fieldStates[field.key] as FieldStateEntry | undefined}
                    isActive={activeField === field.key}
                    onCommit={(v, action) => onCommit(field.key, v, action)}
                    secondary={secondary}
                  />
                );
              })}
            </div>
          </section>
        ))}
        {groups.length === 0 && (
          showDeep ? (
            <div className="aa-dossier-empty-preview">
              <p className="aa-dossier-empty-hint">
                Nothing deeper is needed yet. If Orbit decides this problem
                needs a deeper pass, usually for clarity or compliance, those
                blocks (Phase 1 · Company shell, Phase 3 · Outcomes) will
                appear here.
              </p>
            </div>
          ) : (
            <EmptyDossierPreview
              sections={sections}
              fields={fields.filter(f => (
                isProblem ? f.section === 'e' && f.key !== 'e.flag_reason'
                  : COMPANY_SECTIONS.includes(f.section)
              ))}
            />
          )
        )}
      </div>
    </aside>
  );
}


/* Empty-state preview — shown when nothing has been captured yet.
 * Instead of a bland one-liner, render every section + the field labels
 * each will collect so users understand the shape of the dossier before
 * they start. Each section letter renders with its full name (Company,
 * Stack, Org, ...) so the A/B/C aren't bare initials. */
interface EmptyDossierPreviewProps {
  sections: Partial<Record<SectionLetter, string>>;
  fields: FieldDef[];
}

function EmptyDossierPreview({ sections, fields }: EmptyDossierPreviewProps) {
  const grouped: Array<[SectionLetter, FieldDef[]]> = useMemo(() => {
    const by: Partial<Record<SectionLetter, FieldDef[]>> = {};
    for (const f of fields) {
      // Skip the always-paired-with-industry detail row
      if (f.key === 'a.industry_detail') continue;
      const list = by[f.section] ?? [];
      list.push(f);
      by[f.section] = list;
    }
    return (Object.keys(sections) as SectionLetter[])
      .filter(letter => (by[letter] ?? []).length > 0)
      .map(letter => [letter, by[letter] ?? []]);
  }, [sections, fields]);

  return (
    <div className="aa-dossier-empty-preview">
      <p className="aa-dossier-empty-hint">
        Drop something on the left and the dossier will start filling. Here&apos;s what it captures:
      </p>
      {grouped.map(([letter, fs]) => (
        <section key={letter} className="aa-dossier-empty-section">
          <div className="aa-dossier-empty-section-label">
            <span className="aa-dossier-empty-section-letter">{letter.toUpperCase()}</span>
            {sections[letter] ?? letter.toUpperCase()}
          </div>
          <ul className="aa-dossier-empty-fields">
            {fs.map(f => (
              <li key={f.key} className="aa-dossier-empty-field">
                {f.label}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export default DossierPanel;
